/**
 * Estado do painel de artefato (Fase 2b — integração final, plano
 * `moonlit-conjuring-sun`).
 *
 * Hoje um card de dado (`ui.component`) monta o componente REAL direto na
 * bolha do chat. A ideia é trocar isso por um card de prévia compacto que,
 * ao clicar, abre um aside ao lado do chat com o componente de verdade — só
 * na visão imersiva (`/home-ia`).
 *
 * `parte` guarda `{ componente, props, label, module }` — o mesmo objeto que
 * hoje vai direto pro `<component :is>` inline, só que retido até o clique
 * abrir o aside. `renderiza-partes.js`/`chat-message.vue`/
 * `view-chat-imersivo.vue` ligam essa fiação na Fase 2b.
 *
 * Por quê Vuex e não prop/evento direto: quem DISPARA a abertura
 * (`chat-message.vue`, fundo da árvore) e quem MONTA o aside
 * (`view-chat-imersivo.vue`, topo da árvore) são primos, não pai/filho.
 *
 * `modoAtivo` é o segundo eixo do gate (Fase 2b): `modoArtefatoAtivo` só é
 * `true` quando a constante de rollout `ARTEFATO_MODO_ATIVO`
 * (`constants/Feature.js`, o kill-switch de código) E `modoAtivo` (ligado
 * pela visão imersiva no `mounted()`/desligado no `beforeDestroy()`) estão os
 * dois verdadeiros — o mixin `renderiza-partes.js` lê só este getter, nunca a
 * constante direto, e é isso que mantém Fee-Fixo/drawer/guia e a rota
 * `/aawz-intelligence/chat-ia` (que nunca liga `modoAtivo`) desenhando o
 * cartão real como sempre desenharam.
 *
 * ★ Módulo autocontido, composto direto no `store/index.js` do módulo — mesmo
 * movimento do `toolParts.js` (ver o header de lá): como este slice NÃO é
 * dispatchado por `apiChat.js` (não é despachado por namespace de superfície),
 * não precisa ser reexportado por `state.js`/`actions.js`/`getters.js`/
 * `mutations.js`, e evita tocar esses quatro arquivos, hoje sob edição
 * concorrente por outras sessões.
 *
 * ## O índice "Artefatos desta conversa" (`artefatosDaConversa`)
 *
 * A lista de TODOS os artefatos da conversa aberta, para o popover do trilho
 * (`components/chat-indice-artefatos/`) — nos dois modos, prévia (aside) e
 * inline (o chip rola até o cartão; gate `indiceDeArtefatosAtivo`). Quem
 * registra é a bolha (`chat-message.vue`), a cada cartão de dados que ela DE
 * FATO desenha — e não um
 * util que releia `messages[].tool_calls`, porque a decisão de "isto vira
 * prévia" passa por `$access`, pelo veredito assíncrono do schema
 * (`validacoes`) e pelos ramos de selo/recibo/falha do mixin: refazer isso
 * fora dele seria duplicar a regra, e ler só o `tool_calls` listaria cartão
 * que na tela virou `fallback-card`. O registro é idempotente por
 * `identidade` (a mesma do `:key` do renderer), e a lista é LIMPA ao trocar
 * ou iniciar conversa (`view-chat.vue`, ao lado do `fecharArtefato`).
 *
 * ## Aberto sozinho (kill-switch `PAINEL_PROGRESSO_CONTEXTO_ATIVO`)
 *
 * O aside só mostra entrega. O painel da conversa (Progresso / Entregas /
 * Contexto) é o cartão flutuante do trilho, não um modo do aside. Quando o
 * turno com plano termina com entrega nova, o `messages.vue` abre ela pela
 * MESMA `abrirArtefato` do clique — uma largura só para as duas aberturas
 * (`FRACAO_DA_LARGURA_DO_ARTEFATO`, `ui-registry/artefato-painel.vue`); o
 * `asideCompacto`/`abrirArtefatoCompacto` (largura mínima) saiu em 17/09/2026.
 * Com a flag desligada ninguém despacha a action e nada muda.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { ARTEFATO_MODO_ATIVO } from '@modules/aawz-intelligence-v2/constants/Feature'
import { ordenarArtefatosPelasMensagens } from '@modules/aawz-intelligence-v2/utils/artefatos-da-conversa'

export function criarStateArtefato() {
  return {
    aberto: false,
    parte: null,
    // Ligado/desligado pela visão imersiva — nasce `false` para que qualquer
    // outra superfície (Fee Fixo, `chat-ia` de página cheia) continue
    // renderizando o cartão real, mesmo com o kill-switch de código ligado.
    modoAtivo: false,
    // `[{identidade, titulo, icone, subtitulo, modulo, mensagemId, criadoEm,
    // parteReal}]` — um por cartão de dados desenhado na conversa aberta
    // (`parteReal` na prévia E no inline; `null` só em cartão de escrita ou
    // sem painel na superfície), na ordem em que foram
    // registrados (a ordem das bolhas). Ver o cabeçalho.
    artefatosDaConversa: [],
    // ★ **[18/09/2026] Quais entregas já abriram o aside SOZINHAS.**
    //
    // `renderiza-partes.js` guardava isso num mapa de `data()`
    // (`documentosAbertosAutomaticamente`), e o comentário de lá declara o
    // alcance: *"este mapa cobre o replay DENTRO da mesma montagem; a guarda
    // de `persistida` cobre histórico/remontagem"*. Sobra um terceiro caso, e
    // é o que o owner mediu: a parte VIVA continua no slice `toolParts` depois
    // que o turno fecha, então voltar ao chat SEM F5 remonta o renderer (mapa
    // local zerado) com uma parte que ainda não é persistida — e o
    // `/panorama` reabre sozinho, mesmo tendo sido FECHADO à mão.
    //
    // No store a marca sobrevive à remontagem e morre junto com a conversa
    // (`LIMPAR_ARTEFATOS_DA_CONVERSA`), que é exatamente o tempo de vida que
    // ela deve ter: abrir sozinho é um gesto do TURNO, uma vez só.
    artefatosAbertosAutomaticamente: {},
  }
}

function indiceDoArtefato(lista, identidade) {
  for (let i = 0; i < lista.length; i += 1) {
    if (lista[i].identidade === identidade) return i
  }
  return -1
}

export const artefatoMutations = {
  SET_ARTEFATO_ABERTO(state, payload) {
    state.aberto = Boolean(payload)
  },
  SET_ARTEFATO_PARTE(state, payload) {
    state.parte = payload || null
  },
  SET_ARTEFATO_MODO_ATIVO(state, payload) {
    state.modoAtivo = Boolean(payload)
  },
  // Idempotente por `identidade`: a prévia da bolha em streaming e a da
  // mensagem persistida têm o MESMO `toolCallId`, e a segunda só completa a
  // primeira (ganha `mensagemId`/`criadoEm`) — no lugar, sem duplicar nem
  // mudar de posição. `splice` (não atribuição por índice) para o Vue 2
  // enxergar a troca.
  REGISTRAR_ARTEFATO_DA_CONVERSA(state, payload) {
    if (!payload || !payload.identidade) return
    const lista = state.artefatosDaConversa || []
    const indice = indiceDoArtefato(lista, payload.identidade)
    if (indice === -1) {
      state.artefatosDaConversa = lista.concat([payload])
      return
    }
    lista.splice(indice, 1, payload)
  },
  // A prévia que a bolha DEIXOU de desenhar (o veredito do schema chegou
  // `false` e ela virou `fallback-card`) sai da lista — o índice não pode
  // oferecer um artefato que a tela já recusou.
  REMOVER_ARTEFATO_DA_CONVERSA(state, identidade) {
    if (!identidade) return
    state.artefatosDaConversa = (state.artefatosDaConversa || []).filter(function (artefato) {
      return artefato.identidade !== identidade
    })
  },
  MARCAR_ARTEFATO_ABERTO_AUTOMATICAMENTE(state, identidade) {
    if (!identidade) return
    // Objeto novo: o Vue 2 não detecta chave acrescentada sem `$set`.
    state.artefatosAbertosAutomaticamente = {
      ...state.artefatosAbertosAutomaticamente,
      [identidade]: true,
    }
  },
  LIMPAR_ARTEFATOS_DA_CONVERSA(state) {
    state.artefatosDaConversa = []
    // A marca de abertura automática pertence à conversa que está saindo.
    state.artefatosAbertosAutomaticamente = {}
  },
}

export const artefatoGetters = {
  // Se o aside está visível. Sempre booleano, nunca undefined.
  artefatoAberto: (state) => Boolean(state.aberto),

  // A parte { componente, props, label, module } que o aside deve renderizar,
  // ou null quando não há nenhuma retida.
  artefatoAtivo: (state) => state.parte || null,

  // "Há painel disponível nesta superfície" — condição NECESSÁRIA, não
  // suficiente, para `renderiza-partes.js::montarCartao` trocar o cartão real
  // por `ArtefatoPreviewCard`: falso ⇒ tudo inline; verdadeiro ⇒ quem decide
  // POR CARTÃO é o backend, via `ui.apresentacao` (ausente = painel; a régua
  // é `ui-registry/apresentacao.js`). Os DOIS eixos precisam estar ligados:
  // a constante de rollout (código, o kill-switch de emergência) E o
  // `modoAtivo` (a rota atual é a imersiva). Sem o segundo eixo, ligar a
  // constante globalmente também ligaria o modo artefato no drawer do Fee
  // Fixo e na rota de página cheia.
  modoArtefatoAtivo: (state) => Boolean(ARTEFATO_MODO_ATIVO && state.modoAtivo),

  // O índice "Artefatos desta conversa" vale na imersiva com OU sem o
  // kill-switch: só o segundo eixo (`modoAtivo`). É o gate do registro da
  // bolha e do chip — inline, o chip rola até o cartão em vez de abrir o aside.
  indiceDeArtefatosAtivo: (state) => Boolean(state.modoAtivo),

  // A identidade do artefato ATIVO (aberto de fato no aside), ou `null`.
  // Fechar o aside faz esta identidade voltar a `null` — mesmo com `parte`
  // ainda retida (ver `fecharArtefato`) — porque "ativo" é sobre estar
  // visível AGORA, não sobre "foi o último aberto".
  artefatoIdentidadeAtiva: (state) => {
    if (!state.aberto) return null
    return (state.parte && state.parte.identidade) || null
  },

  // Os artefatos da conversa aberta, na ordem das MENSAGENS. Este slice é
  // composto no namespace raiz (`store/index.js`), então `state.messages`
  // está ao alcance e é ele que dá a ordem; registrado como sub-módulo
  // (sem `messages`) cai na ordem de registro, que já é a das bolhas.
  // Sempre array, nunca undefined.
  artefatosDaConversa: (state) =>
    ordenarArtefatosPelasMensagens(state.artefatosDaConversa, state.messages),

  // "Esta entrega já abriu o aside sozinha nesta conversa?" — a guarda que
  // sobrevive à remontagem do renderer. Ver `artefatosAbertosAutomaticamente`.
  artefatoJaAbriuAutomaticamente: (state) => (identidade) =>
    Boolean(identidade && state.artefatosAbertosAutomaticamente[identidade]),
}

export const artefatoActions = {
  // Guarda a parte e abre o aside — nesta ordem, para que o getter
  // `artefatoAtivo` já tenha o que mostrar no mesmo tick em que `artefatoAberto`
  // vira true. Vale para as DUAS aberturas — o clique do usuário (prévia,
  // índice, cartão do painel) e a automática do fim de turno: as duas abrem na
  // MESMA largura (`FRACAO_DA_LARGURA_DO_ARTEFATO`, `artefato-painel.vue`).
  abrirArtefato({ commit }, parte) {
    commit('SET_ARTEFATO_PARTE', parte || null)
    commit('SET_ARTEFATO_ABERTO', true)
  },

  // Fecha o aside. Não limpa `parte` de propósito: fechar/reabrir rápido (ou
  // uma transição de saída que ainda lê o conteúdo) não perde o que estava
  // sendo mostrado.
  fecharArtefato({ commit }) {
    commit('SET_ARTEFATO_ABERTO', false)
  },

  // Ligado no `mounted()` da visão imersiva — é o que distingue "modo
  // artefato existe no código" de "modo artefato vale NESTA tela".
  ativarModoArtefato({ commit }) {
    commit('SET_ARTEFATO_MODO_ATIVO', true)
  },

  // Desligado no `beforeDestroy()` da visão imersiva — sem isto, sair da
  // `/home-ia` para o `chat-ia` de página cheia (mesmo namespace Vuex, store
  // não é resetada na troca de rota) manteria o modo artefato ligado lá.
  desativarModoArtefato({ commit }) {
    commit('SET_ARTEFATO_MODO_ATIVO', false)
  },

  // Despachada pela bolha a cada cartão de dados desenhado — prévia ou inline
  // (ver o cabeçalho) — pode repetir à vontade, a mutation é idempotente por
  // `identidade`.
  registrarArtefatoDaConversa({ commit }, artefato) {
    commit('REGISTRAR_ARTEFATO_DA_CONVERSA', artefato || null)
  },

  removerArtefatoDaConversa({ commit }, identidade) {
    commit('REMOVER_ARTEFATO_DA_CONVERSA', identidade || null)
  },

  // Trocar/iniciar conversa: a lista é DESTA conversa, e a bolha da próxima
  // registra os dela do zero.
  limparArtefatosDaConversa({ commit }) {
    commit('LIMPAR_ARTEFATOS_DA_CONVERSA')
  },

  // Chamada pelo renderer no instante em que ele despacha a abertura
  // automática — nunca pelo clique, que é gesto do usuário e pode se repetir.
  marcarArtefatoAbertoAutomaticamente({ commit }, identidade) {
    commit('MARCAR_ARTEFATO_ABERTO_AUTOMATICAMENTE', identidade || null)
  },
}

// Módulo autocontido, para quem preferir registrar o slice namespaced
// (`<ns>/artefato/*`) em vez de compor no namespace raiz. `state` é função,
// mesma disciplina de `toolParts.js`/`hub-e-agente.js`: dois registros nunca
// compartilham a mesma referência de objeto.
export default {
  namespaced: true,
  state: criarStateArtefato,
  getters: artefatoGetters,
  mutations: artefatoMutations,
  actions: artefatoActions,
}
